import type {
  ContentTable,
  StyleDictionary,
  TableLayout,
  TDocumentDefinitions,
} from 'pdfmake/interfaces';

const CM_TO_PT = 28.3464566929;
export const cm = (value: number) => Number((value * CM_TO_PT).toFixed(2));

// LUCAMILANESI Brand Colors
export const pdfColors = {
  brandOrange: '#F98F26',
  brandBlue: '#041F7F',
  black: '#000000',
  white: '#FFFFFF',
  lightGray: '#F5F5F5',
  borderGray: '#000000',
  darkGray: '#333333',
  yellow: '#FFF9C4',
  diffAdded: '#E6F4EA',
  diffRemoved: '#999999',
  noteGray: '#555555',
} as const;

// Note: pdfmake bundles Roboto fonts via its virtual file system (vfs_fonts)
// The export hook loads those fonts at runtime so styles can rely on them

// Page configuration
const pageConfig = {
  pageSize: 'A4' as const,
  pageOrientation: 'portrait' as const,
  pageMargins: [cm(1.5), cm(2.95), cm(1.5), cm(1.3)] as [number, number, number, number],
} as const;

// Named styles for consistent formatting
const pdfStyles: StyleDictionary = {
  // Headers and titles
  documentTitle: {
    fontSize: 13,
    bold: true,
    alignment: 'center',
    margin: [0, 0, 0, 20],
    decoration: 'underline',
    decorationStyle: 'solid',
    color: pdfColors.black,
  },

  sectionHeader: {
    fontSize: 11,
    bold: true,
    fillColor: '#F2F2F2',
    margin: [0, cm(0.4), 0, cm(0.25)],
    color: pdfColors.black,
  },

  companyName: {
    fontSize: 14,
    bold: true,
    color: pdfColors.brandBlue,
  },

  // Table styles
  tableHeader: {
    fontSize: 8.5,
    bold: true,
    color: pdfColors.black,
    alignment: 'center',
  },

  tableCell: {
    fontSize: 8.5,
    color: pdfColors.black,
  },

  tableCellLabel: {
    fontSize: 8.5,
    bold: true,
    color: pdfColors.black,
  },

  rowGroupLabel: {
    fontSize: 9,
    bold: true,
    color: pdfColors.black,
    fillColor: pdfColors.lightGray,
  },

  rowGroupDescription: {
    fontSize: 8,
    italics: true,
    color: pdfColors.darkGray,
  },

  // Form elements
  checkbox: {
    fontSize: 8,
    color: pdfColors.black,
    alignment: 'center',
  },

  checkboxLabel: {
    fontSize: 9,
    color: pdfColors.black,
    margin: [3, 0, 0, 0],
  },

  // Text variants
  normal: {
    fontSize: 9,
    color: pdfColors.black,
  },

  small: {
    fontSize: 8,
    color: pdfColors.darkGray,
  },

  bold: {
    bold: true,
    color: pdfColors.black,
  },

  italic: {
    italics: true,
    color: pdfColors.black,
  },

  // Sidebar text
  sidebarText: {
    fontSize: 8,
    bold: true,
    color: pdfColors.white,
    alignment: 'center',
  },

  // Footer
  footer: {
    fontSize: 9,
    color: pdfColors.darkGray,
    alignment: 'justify',
  },

  footerText: {
    fontSize: 7.5,
    color: '#333333',
  },

  // Signature elements
  signatureTitle: {
    fontSize: 10,
    bold: true,
    color: pdfColors.black,
    alignment: 'center',
    margin: [0, 0, 0, 20],
  },

  signatureLine: {
    fontSize: 9,
    color: pdfColors.black,
    alignment: 'center',
    margin: [0, 30, 0, 5],
  },

  heroCompanyName: {
    fontSize: 14,
    bold: true,
    color: pdfColors.black,
  },

  subsectionTitle: {
    fontSize: 10,
    bold: true,
    color: pdfColors.black,
  },

  // Chart styles
  chartTitle: {
    fontSize: 10,
    bold: true,
    color: pdfColors.black,
    alignment: 'center',
    margin: [0, 0, 0, 10],
  },

  chartAxis: {
    fontSize: 8,
    color: pdfColors.darkGray,
  },

  // Change highlighting
  highlighted: {
    fillColor: pdfColors.yellow,
    color: pdfColors.black,
  },

  changeAdded: {
    fillColor: pdfColors.diffAdded,
    color: pdfColors.black,
  },

  changeRemoved: {
    color: pdfColors.diffRemoved,
    decoration: 'lineThrough',
  },

  noteText: {
    fontSize: 9,
    italics: true,
    color: pdfColors.noteGray,
  },

  // Draft watermark
  watermark: {
    color: pdfColors.lightGray,
    fontSize: 60,
    bold: true,
  },
};

// Default document configuration factory
export const createDocumentConfig = (
  title: string,
  isDraft = false
): Partial<TDocumentDefinitions> => ({
  pageSize: pageConfig.pageSize,
  pageOrientation: pageConfig.pageOrientation,
  pageMargins: pageConfig.pageMargins,
  defaultStyle: {
    ...pdfStyles.normal,
    font: 'Roboto',
  },
  styles: pdfStyles,
  info: {
    title: title,
    author: 'LM Sistema Tecnico',
    subject: 'Inspection Report',
    creator: 'LM Sistema Tecnico',
    producer: 'pdfmake',
  },
  ...(isDraft && {
    watermark: {
      text: 'Bozza',
      color: pdfColors.lightGray,
      opacity: 0.3,
      bold: true,
      italics: false,
      fontSize: 60,
      angle: -30,
    },
  }),
});

// Table layout helpers
export const tableLayouts: Record<string, TableLayout> = {
  default: {
    hLineWidth: (i: number, node: ContentTable) => {
      const body = Array.isArray(node.table.body) ? node.table.body : [];
      const bodyLength = body.length;
      return i === 0 || i === bodyLength ? 0.6 : 0.4;
    },
    vLineWidth: (i: number, node: ContentTable) => {
      const widths = Array.isArray(node.table.widths) ? node.table.widths : [];
      const widthCount = widths.length;
      return i === 0 || i === widthCount ? 0.6 : 0.4;
    },
    hLineColor: () => pdfColors.borderGray,
    vLineColor: () => pdfColors.borderGray,
    fillColor: () => null,
    paddingLeft: () => cm(0.1),
    paddingRight: () => cm(0.1),
    paddingTop: () => cm(0.08),
    paddingBottom: () => cm(0.08),
  },

  headerTable: {
    hLineWidth: () => 0.6,
    vLineWidth: () => 0.6,
    hLineColor: () => pdfColors.borderGray,
    vLineColor: () => pdfColors.borderGray,
    fillColor: (i: number) => (i === 0 ? pdfColors.lightGray : null),
    paddingLeft: () => cm(0.1),
    paddingRight: () => cm(0.1),
    paddingTop: () => cm(0.08),
    paddingBottom: () => cm(0.08),
  },

  hero: {
    hLineWidth: () => 0.6,
    vLineWidth: () => 0.6,
    hLineColor: () => pdfColors.borderGray,
    vLineColor: () => pdfColors.borderGray,
    paddingLeft: () => cm(0.12),
    paddingRight: () => cm(0.12),
    paddingTop: () => cm(0.08),
    paddingBottom: () => cm(0.08),
  },

  noBorders: {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 4,
    paddingRight: () => 4,
    paddingTop: () => 2,
    paddingBottom: () => 2,
  },

  tightNoBorders: {
    hLineWidth: () => 0,
    vLineWidth: () => 0,
    paddingLeft: () => 0,
    paddingRight: () => 0,
    paddingTop: () => 0,
    paddingBottom: () => 0,
  },

  dense: {
    hLineWidth: () => 0.5,
    vLineWidth: () => 0.5,
    hLineColor: () => pdfColors.borderGray,
    vLineColor: () => pdfColors.borderGray,
    paddingLeft: () => cm(0.1),
    paddingRight: () => cm(0.1),
    paddingTop: () => cm(0.07),
    paddingBottom: () => cm(0.07),
  },

  formOuter: {
    hLineWidth: (i: number, node: ContentTable) => {
      const body = Array.isArray(node.table.body) ? node.table.body : [];
      const bodyLength = body.length;
      // First row (title): only bottom border if last row
      // Middle rows: no borders (content is wrapped in formInner)
      // Last row: only bottom border
      if (i === 0) return 0.5; // Top border
      if (i === bodyLength) return 0.5; // Bottom border
      return 0; // No horizontal borders between title and content
    },
    vLineWidth: () => 0.5,
    hLineColor: () => pdfColors.borderGray,
    vLineColor: () => pdfColors.borderGray,
    paddingLeft: () => cm(0.08),
    paddingRight: () => cm(0.08),
    paddingTop: () => cm(0.07),
    paddingBottom: () => cm(0.07),
  },

  formInner: {
    hLineWidth: (i: number, node: ContentTable) => {
      const body = Array.isArray(node.table.body) ? node.table.body : [];
      const bodyLength = body.length;
      if (i === 0 || i === bodyLength) return 0;
      return 0.5;
    },
    vLineWidth: (i: number, node: ContentTable) => {
      const widths = Array.isArray(node.table.widths) ? node.table.widths : [];
      const widthCount = widths.length;
      if (i === 0 || i === widthCount) return 0;
      return 0.5;
    },
    hLineColor: () => pdfColors.borderGray,
    vLineColor: () => pdfColors.borderGray,
    paddingLeft: () => cm(0.08),
    paddingRight: () => cm(0.08),
    paddingTop: () => cm(0.07),
    paddingBottom: () => cm(0.07),
  },
};
// Font VFS not needed - using pdfmake defaults to avoid file dependencies
